import React, { useState, useEffect } from 'react';
import GroupEmailModal from '../components/GroupEmailModal';
import EmailServiceModal from '../components/EmailServiceModal';
import ReportModal from '../components/ReportModal';
import ExportModal from '../components/ExportModal';
import { useModals } from '../hooks/useModals';
import { 
  ArrowLeft, 
  Download, 
  Mail,
  AlertTriangle, 
  Phone, 
  Briefcase, 
  GraduationCap, 
  Languages, 
  Star, 
  Eye,
  EyeOff,
  Building,
  Award,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  Target,
  Sparkles,
  Crown,
  Medal,
  Search,
  Filter,
  User,
  Calendar,
  TrendingUp,
  FileText,
  Share2,
  Send,
  ExternalLink,
  Clock,
  MapPin,
  Globe,
  Zap,
  Shield,
  Heart,
  Brain,
  Lightbulb,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Users,
  Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScreening } from '../context/ScreeningContext';

// Header simple inline
const SimpleHeader = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-2xl border-b border-white/10 shadow-2xl">
    <div className="max-w-7xl mx-auto px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900 animate-pulse"></div>
          </div>
          <div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-pink-400 to-blue-400 bg-clip-text text-transparent">
              Resume Screening Pro
            </h1>
            <p className="text-gray-400 text-sm">Intelligence Artificielle Avancée</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full">
            <span className="text-green-300 text-sm font-medium">IA Active</span>
          </div>
        </div>
      </div>
    </div>
  </header>
);

// Particules premium
const PremiumParticles = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
    <div className="absolute top-3/4 right-1/4 w-80 h-80 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
    <div className="absolute bottom-1/4 left-1/2 w-72 h-72 bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
    <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-full blur-3xl animate-pulse delay-3000"></div>
  </div>
);

// Composant synthèse RH
const RHSynthesis = ({ candidate }) => {
    const getSynthesisIcon = (score) => {
      if (score >= 90) return <Crown className="w-6 h-6 text-yellow-400" />;
      if (score >= 80) return <Star className="w-6 h-6 text-green-400" />;
      if (score >= 70) return <ThumbsUp className="w-6 h-6 text-blue-400" />;
      if (score >= 50) return <MessageSquare className="w-6 h-6 text-yellow-400" />;
      return <ThumbsDown className="w-6 h-6 text-red-400" />;
    };
  
    const getSynthesisColor = (score) => {
      if (score >= 80) return 'from-green-500/20 to-emerald-500/20 border-green-500/30';
      if (score >= 70) return 'from-blue-500/20 to-cyan-500/20 border-blue-500/30';
      if (score >= 50) return 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30';
      return 'from-red-500/20 to-pink-500/20 border-red-500/30';
    };
  
    const getRecommendationBadge = (recommendation) => {
      const rec = recommendation?.toLowerCase() || '';
      if (rec.includes('fortement') || rec.includes('recommande_fortement')) {
        return (
          <div className="inline-flex items-center px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-full">
            <Crown className="w-4 h-4 text-green-400 mr-2" />
            <span className="text-green-300 font-semibold">Fortement Recommandé</span>
          </div>
        );
      }
      if (rec.includes('recommande')) {
        return (
          <div className="inline-flex items-center px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-full">
            <ThumbsUp className="w-4 h-4 text-blue-400 mr-2" />
            <span className="text-blue-300 font-semibold">Recommandé</span>
          </div>
        );
      }
      if (rec.includes('evaluer') || rec.includes('neutre')) {
        return (
          <div className="inline-flex items-center px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full">
            <AlertCircle className="w-4 h-4 text-yellow-400 mr-2" />
            <span className="text-yellow-300 font-semibold">À Évaluer</span>
          </div>
        );
      }
      return (
        <div className="inline-flex items-center px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full">
          <XCircle className="w-4 h-4 text-red-400 mr-2" />
          <span className="text-red-300 font-semibold">Non Recommandé</span>
        </div>
      );
    };
  
    return (
      <div className={`relative p-8 bg-gradient-to-br ${getSynthesisColor(candidate.score || 0)} backdrop-blur-xl border rounded-3xl shadow-2xl mb-8 overflow-hidden`}>
        
        {/* Effet de brillance d'arrière-plan */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12 translate-x-full group-hover:translate-x-[-100%] transition-transform duration-1000"></div>
        
        {/* Header avec IA */}
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
                  {getSynthesisIcon(candidate.score || 0)}
                </div>
                {/* Indicateur IA animé */}
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-pulse">
                  <Brain className="w-3 h-3 text-white" />
                </div>
              </div>
              <div>
              <h4 className="text-2xl font-bold text-white mb-1 flex items-center">
                🤖 Analyse IA Expert RH
                <span className="ml-3 px-3 py-1 bg-purple-500/30 border border-purple-400/50 rounded-full text-purple-200 text-sm font-medium">
                    IA Expert
                </span>
                </h4>
                <p className="text-gray-400 text-sm flex items-center">
                  <Zap className="w-4 h-4 mr-2 text-yellow-400" />
                  Analyse avancée • {candidate.score || 0}% de correspondance
                </p>
              </div>
            </div>
          </div>
  
          {/* Sections d'analyse détaillées */}
          <div className="space-y-6">
            
            {/* Avis Expert RH */}
            <div className="p-6 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-sm">
                <h5 className="text-white font-bold mb-3 flex items-center text-lg">
                    <User className="w-5 h-5 mr-3 text-blue-400" />
                    👨‍💼 Avis Expert RH sur le Profil
                </h5>
                <p className="text-gray-200 leading-relaxed italic">
                    "{candidate.avis_expert_rh || "Profil en cours d'évaluation par l'expert RH."}"
                </p>
                </div>

                <div className="p-6 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-sm">
                <h5 className="text-white font-bold mb-3 flex items-center text-lg">
                    <Target className="w-5 h-5 mr-3 text-purple-400" />
                    🎯 Correspondance aux Exigences du Poste
                </h5>
                <p className="text-gray-200 leading-relaxed">
                    {candidate.analyse_correspondance || "Analyse de correspondance en cours."}
                </p>
                </div>
  
            {/* Points forts et faibles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-green-500/10 rounded-2xl border border-green-500/30">
                <h5 className="text-green-300 font-bold mb-4 flex items-center text-lg">
                  <ThumbsUp className="w-5 h-5 mr-3" />
                  💪 Points Forts Identifiés
                </h5>
                <div className="space-y-2">
                  {candidate.points_forts_objectifs && candidate.points_forts_objectifs.length > 0 ? (
                    candidate.points_forts_objectifs.map((point, i) => (
                      <div key={i} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-green-200 text-sm">{point}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-green-300 text-sm italic">Points forts en cours d'analyse...</p>
                  )}
                </div>
              </div>
  
              <div className="p-6 bg-yellow-500/10 rounded-2xl border border-yellow-500/30">
                <h5 className="text-yellow-300 font-bold mb-4 flex items-center text-lg">
                  <AlertCircle className="w-5 h-5 mr-3" />
                  ⚠️ Points d'Amélioration
                </h5>
                <div className="space-y-2">
                {candidate.points_faibles_objectifs && candidate.points_faibles_objectifs.length > 0 && 
                !candidate.points_faibles_objectifs.includes('Erreur technique') && (
                <div className="p-6 bg-yellow-500/10 rounded-2xl border border-yellow-500/30">
                    <h5 className="text-yellow-300 font-bold mb-4 flex items-center text-lg">
                    <AlertCircle className="w-5 h-5 mr-3" />
                    ⚠️ Points d'Amélioration
                    </h5>
                    <div className="space-y-2">
                    {candidate.points_faibles_objectifs.map((point, i) => (
                        <div key={i} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-yellow-200 text-sm">{point}</span>
                        </div>
                    ))}
                    </div>
                </div>
                )}
                </div>
              </div>
            </div>
  

            {(candidate.statut === 'DISQUALIFIÉ' || candidate.score === 0) && (
  <div className="p-6 bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-500/30 rounded-2xl">
    <h5 className="text-red-300 font-bold mb-4 flex items-center text-lg">
      <XCircle className="w-5 h-5 mr-3" />
      🚫 Candidat Disqualifié
    </h5>
    
    {/* Raisons multiples de disqualification */}
    <div className="space-y-4">
      {candidate.criteres_disqualifiants?.liste_complete && candidate.criteres_disqualifiants.liste_complete.length > 0 ? (
        <div>
          <p className="text-red-200 font-medium mb-3">
            {candidate.criteres_disqualifiants.liste_complete.length === 1 ? 'Raison de la disqualification :' : 'Raisons de la disqualification :'}
          </p>
          <div className="space-y-2">
            {candidate.criteres_disqualifiants.liste_complete.map((raison, i) => (
              <div key={i} className="flex items-start space-x-3 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <span className="text-red-100 text-sm font-medium">{raison}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
          <p className="text-red-200 font-medium mb-2">Raison de la disqualification :</p>
          <p className="text-red-100 text-sm">
            {candidate.criteres_disqualifiants?.details || 
             candidate.avis_expert_rh || 
             'Candidat ne respectant pas les critères éliminatoires définis'}
          </p>
        </div>
      )}
      
      {/* Détails des vérifications */}
      {candidate.criteres_disqualifiants?.verification_complete && (
        <div className="mt-4">
          <p className="text-red-300 font-medium text-sm mb-2">Détail des vérifications :</p>
          <div className="grid grid-cols-1 gap-2">
            {candidate.criteres_disqualifiants.verification_complete.map((verif, i) => (
              <div key={i} className={`flex items-center space-x-2 p-2 rounded text-xs ${
                verif.respecte 
                  ? 'bg-green-500/20 text-green-200' 
                  : verif.gravite === 'ÉLIMINATOIRE' 
                    ? 'bg-red-500/30 text-red-200 font-medium' 
                    : 'bg-yellow-500/20 text-yellow-200'
              }`}>
                {verif.respecte ? (
                  <CheckCircle className="w-3 h-3 text-green-400" />
                ) : verif.gravite === 'ÉLIMINATOIRE' ? (
                  <XCircle className="w-3 h-3 text-red-400" />
                ) : (
                  <AlertCircle className="w-3 h-3 text-yellow-400" />
                )}
                <span>{verif.critere}: {verif.details}</span>
                    </div>
                    ))}
                </div>
                </div>
            )}
            </div>
        </div>
        )}

            {/* Critères respectés/non respectés */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-green-500/10 rounded-2xl border border-green-500/30">
                    <h5 className="text-green-300 font-bold mb-4 flex items-center text-lg">
                    <CheckCircle className="w-5 h-5 mr-3" />
                    ✅ Critères Respectés
                    </h5>
                    <div className="space-y-3">
                    {candidate.correspondance_criteres && candidate.correspondance_criteres.length > 0 ? (
                        candidate.correspondance_criteres.map((critere, i) => (
                        <div key={i} className="flex items-start space-x-3 p-3 bg-green-500/20 rounded-lg">
                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-green-200 text-sm font-medium">{critere}</span>
                        </div>
                        ))
                    ) : (
                        <p className="text-green-300 text-sm italic">Critères en cours d'évaluation...</p>
                    )}
                    </div>
                </div>

                {/* Critères non respectés - seulement si présents */}
                {candidate.criteres_non_respectes && candidate.criteres_non_respectes.length > 0 && (
                    <div className="p-6 bg-red-500/10 rounded-2xl border border-red-500/30">
                    <h5 className="text-red-300 font-bold mb-4 flex items-center text-lg">
                        <XCircle className="w-5 h-5 mr-3" />
                        ❌ Critères Non Respectés
                    </h5>
                    <div className="space-y-3">
                        {candidate.criteres_non_respectes.map((critere, i) => (
                        <div key={i} className="flex items-start space-x-3 p-3 bg-red-500/20 rounded-lg">
                            <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                            <span className="text-red-200 text-sm font-medium">{critere}</span>
                        </div>
                        ))}
                    </div>
                    </div>
                )}

                {/* Message de félicitations si tous respectés */}
                {(!candidate.criteres_non_respectes || candidate.criteres_non_respectes.length === 0) && (
                    <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl text-center">
                    <div className="flex items-center justify-center space-x-2 text-green-300">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">🎉 Excellent ! Tous les critères sont respectés par ce candidat.</span>
                    </div>
                    </div>
                )}
                </div>
  
            {/* Recommandation finale détaillée */}
            <div className="p-6 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl">
              <h5 className="text-white font-bold mb-4 flex items-center text-lg">
                <Lightbulb className="w-5 h-5 mr-3 text-yellow-400" />
                💡 Recommandation Finale & Justification
              </h5>
              <div className="space-y-4">
  <div className="flex items-start space-x-4">
    <div className="flex-shrink-0">
      <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
        <Award className="w-6 h-6 text-white" />
      </div>
    </div>
    <div className="space-y-4">
      <div>
        <p className="text-purple-200 font-medium mb-3">
          <strong>🤖 Décision IA Expert :</strong> 
          <span className="ml-2 px-3 py-1 bg-purple-500/30 rounded-full text-sm">
            {candidate.recommandation_finale || "En évaluation"}
          </span>
        </p>
      </div>
      
      {/* Justification de la décision */}
      <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
        <h6 className="text-orange-300 font-medium mb-2 flex items-center">
          <Lightbulb className="w-4 h-4 mr-2" />
          Justification de la décision
        </h6>
        <p className="text-orange-100 text-sm leading-relaxed">
          {candidate.justification_recommandation || 
           "Décision basée sur l'évaluation complète du profil selon les critères définis."}
        </p>
      </div>
      
      {/* NOUVELLE SECTION : Comparaison profil/poste */}
            <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg">
                <h6 className="text-blue-300 font-medium mb-3 flex items-center">
                <Target className="w-4 h-4 mr-2" />
                🎯 Adéquation Profil vs Poste Recherché
                </h6>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                    <span className="text-gray-400">Poste recherché :</span>
                    <p className="text-white font-medium">{candidate.criteriaUsed?.selectedProfile || 'Non défini'}</p>
                </div>
                <div>
                    <span className="text-gray-400">Score d'adéquation :</span>
                    <p className="text-white font-medium">{candidate.adequation_poste || `${candidate.score || 0}%`}</p>
                </div>
                <div>
                    <span className="text-gray-400">Profil candidat :</span>
                    <p className="text-white font-medium">{candidate.experience_totale || 0} ans d'expérience</p>
                </div>
                <div>
                    <span className="text-gray-400">Correspondance :</span>
                    <p className={`font-medium ${
                    (candidate.score || 0) >= 80 ? 'text-green-400' :
                    (candidate.score || 0) >= 60 ? 'text-blue-400' :
                    (candidate.score || 0) >= 40 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {(candidate.score || 0) >= 80 ? 'Excellente' :
                    (candidate.score || 0) >= 60 ? 'Bonne' :
                    (candidate.score || 0) >= 40 ? 'Moyenne' : 'Faible'}
                    </p>
                </div>
                </div>
            </div>
            
            <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                <p className="text-gray-200 leading-relaxed italic">
                <strong>💡 Analyse comparative :</strong> {
                    candidate.avis_expert_rh || 
                    candidate.justification_recommandation || 
                    candidate.analyse_correspondance ||
                    `Candidat évalué avec un score de ${candidate.score || 0}/100. Le profil correspond ${
                    (candidate.score || 0) >= 70 ? 'très bien' :
                    (candidate.score || 0) >= 50 ? 'correctement' : 'partiellement'
                    } aux exigences du poste recherché.`
                }
                </p>
                {candidate.expert_rh && (
                <div className="mt-3 flex items-center text-purple-300 text-sm">
                    <Brain className="w-4 h-4 mr-2" />
                    <span>Analyse effectuée par IA Expert RH</span>
                </div>
                )}
            </div>
            </div>
        </div>
        </div>
            </div>
  
            {/* Métadonnées d'analyse */}
            <div className="flex items-center justify-between pt-6 border-t border-white/20">
              <div className="flex items-center space-x-4 text-gray-400 text-sm">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>Analysé le {new Date().toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Activity className="w-4 h-4" />
                  <span>Score de confiance: {Math.min(95, (candidate.score || 0) + 10)}%</span>
                </div>
              </div>
              <div className="flex items-center space-x-2 px-4 py-2 bg-white/10 rounded-lg">
                <Brain className="w-4 h-4 text-purple-400" />
                <span className="text-purple-300 text-sm font-medium">RH IA Expert</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

// Composant carte candidat premium
const PremiumCandidateCard = ({ candidate, index, isExpanded, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const getScoreColor = (score) => {
    if (score >= 80) return 'from-green-500 to-emerald-600';
    if (score >= 60) return 'from-blue-500 to-cyan-600';
    if (score >= 40) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-pink-600';
  };

  const getScoreIcon = (score) => {
    if (score >= 90) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (score >= 80) return <Medal className="w-5 h-5 text-green-400" />;
    if (score >= 60) return <Star className="w-5 h-5 text-blue-400" />;
    return <Target className="w-5 h-5 text-gray-400" />;
  };

  const getStatusBadge = (statut, score) => {
    if (statut === 'ELIMINE_AUTOMATIQUEMENT') {
      return (
        <div className="flex items-center px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full backdrop-blur-sm">
          <XCircle className="w-4 h-4 text-red-400 mr-2" />
          <span className="text-red-300 text-sm font-medium">Éliminé</span>
        </div>
      );
    }
    if (score >= 80) {
      return (
        <div className="flex items-center px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-full backdrop-blur-sm">
          <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
          <span className="text-green-300 text-sm font-medium">Fortement Recommandé</span>
        </div>
      );
    }
    if (score >= 60) {
      return (
        <div className="flex items-center px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-full backdrop-blur-sm">
          <CheckCircle className="w-4 h-4 text-blue-400 mr-2" />
          <span className="text-blue-300 text-sm font-medium">Recommandé</span>
        </div>
      );
    }
    if (score >= 40) {
      return (
        <div className="flex items-center px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full backdrop-blur-sm">
          <AlertCircle className="w-4 h-4 text-yellow-400 mr-2" />
          <span className="text-yellow-300 text-sm font-medium">À Évaluer</span>
        </div>
      );
    }
    return (
      <div className="flex items-center px-4 py-2 bg-gray-500/20 border border-gray-500/30 rounded-full backdrop-blur-sm">
        <XCircle className="w-4 h-4 text-gray-400 mr-2" />
        <span className="text-gray-300 text-sm font-medium">Non Retenu</span>
      </div>
    );
  };

  return (
    <div className="group relative">
      {/* Effet brillance pour top candidats */}
      {candidate.score >= 80 && (
        <div className="absolute -inset-2 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 animate-pulse"></div>
      )}
      
      <div 
        className={`relative cursor-pointer transition-all duration-500 ${
          isExpanded 
            ? 'bg-gradient-to-br from-gray-800/95 to-gray-900/95 scale-[1.02] shadow-2xl' 
            : 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 hover:from-gray-800/95 hover:to-gray-900/95'
        } backdrop-blur-2xl border border-white/10 rounded-2xl hover:border-white/20 hover:shadow-2xl hover:scale-[1.01]`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onToggle}
      >
        {/* Indicateur d'expansion */}
        <div className="absolute top-4 right-4 opacity-60 group-hover:opacity-100 transition-opacity">
          {isExpanded ? (
            <EyeOff className="w-5 h-5 text-gray-400" />
          ) : (
            <Eye className="w-5 h-5 text-gray-400" />
          )}
        </div>

        <div className="p-8">
          {/* Header principal */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${getScoreColor(candidate.score || 0)} flex items-center justify-center text-white font-bold text-xl shadow-xl`}>
                  #{index + 1}
                </div>
                {candidate.score >= 80 && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-yellow-900" />
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center space-x-3 mb-1">
                  <h3 className="text-2xl font-bold text-white">
                    {candidate.prenom || 'Prénom'} {candidate.nom || 'Nom'}
                  </h3>
                  {getScoreIcon(candidate.score || 0)}
                </div>
                <p className="text-gray-400 text-sm mb-2">{candidate.fileName}</p>
                <div className="flex items-center space-x-2 text-gray-500 text-xs">
                  <Clock className="w-4 h-4" />
                  <span>Analysé par RH IA Expert</span>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className={`text-4xl font-bold bg-gradient-to-r ${getScoreColor(candidate.score || 0)} bg-clip-text text-transparent mb-2`}>
                {candidate.score || 0}%
              </div>
              {getStatusBadge(candidate.statut, candidate.score || 0)}
            </div>
          </div>

          {/* Informations de contact premium */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {candidate.email && (
              <div className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                <Mail className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-gray-400 text-xs">Email</p>
                  <p className="text-white text-sm font-medium">{candidate.email}</p>
                </div>
              </div>
            )}
            {candidate.telephone && (
              <div className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                <Phone className="w-5 h-5 text-green-400" />
                <div>
                  <p className="text-gray-400 text-xs">Téléphone</p>
                  <p className="text-white text-sm font-medium">{candidate.telephone}</p>
                </div>
              </div>
            )}
            <div className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg backdrop-blur-sm">
              <Briefcase className="w-5 h-5 text-purple-400" />
              <div>
                <p className="text-gray-400 text-xs">Expérience</p>
                <p className="text-white text-sm font-medium">{candidate.experience_totale || 0} ans</p>
              </div>
            </div>
            {candidate.photo_presente && (
              <div className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg backdrop-blur-sm">
                <User className="w-5 h-5 text-pink-400" />
                <div>
                  <p className="text-gray-400 text-xs">Photo</p>
                  <p className="text-white text-sm font-medium">Présente</p>
                </div>
              </div>
            )}
          </div>

          {/* Compétences highlight */}
          {candidate.competences_techniques && candidate.competences_techniques.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center">
                <Zap className="w-4 h-4 mr-2 text-yellow-400" />
                Compétences Techniques Principales
              </h4>
              <div className="flex flex-wrap gap-2">
                {candidate.competences_techniques.slice(0, 5).map((comp, i) => (
                  <span key={i} className="px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-300 text-sm font-medium">
                    {comp}
                  </span>
                ))}
                {candidate.competences_techniques.length > 5 && (
                  <span className="px-3 py-1 bg-gray-500/20 border border-gray-500/30 rounded-full text-gray-400 text-sm">
                    +{candidate.competences_techniques.length - 5} autres
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Aperçu rapide */}
          <div className="mb-6 p-4 bg-gradient-to-r from-white/5 to-white/10 rounded-xl border border-white/10 backdrop-blur-sm">
            <div className="flex items-start space-x-3">
                <Brain className="w-5 h-5 text-purple-400 mt-0.5" />
                <div>
                <h5 className="text-white font-medium mb-1">Décision IA Expert</h5>
                <p className="text-gray-300 text-sm line-clamp-2">
                    <strong>{candidate.recommandation_finale || 'En cours d\'évaluation'}</strong>
                    {candidate.justification_recommandation && (
                    <span className="block mt-1 text-xs text-gray-400">
                        {candidate.justification_recommandation.substring(0, 100)}...
                    </span>
                    )}
                </p>
                </div>
            </div>
            </div>

          {/* Actions rapides */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-lg text-white font-medium transition-all duration-200 hover:shadow-lg hover:scale-105"
              >
                {isExpanded ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span>{isExpanded ? 'Masquer' : 'Voir Détails'}</span>
              </button>
              <span className="text-gray-500 text-sm">ou cliquez sur la carte</span>
            </div>
            
            <div className="flex space-x-2">
              <button 
                onClick={(e) => e.stopPropagation()}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-lg text-gray-300 hover:text-white transition-all hover:scale-105"
                title="Envoyer un email"
              >
                <Send className="w-4 h-4" />
              </button>
              <button 
                onClick={(e) => e.stopPropagation()}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-lg text-gray-300 hover:text-white transition-all hover:scale-105"
                title="Télécharger le CV"
              >
                <Download className="w-4 h-4" />
              </button>
              <button 
                onClick={(e) => e.stopPropagation()}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-lg text-gray-300 hover:text-white transition-all hover:scale-105"
                title="Partager le profil"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Panneau de détails expandable */}
        {isExpanded && (
          <div className="border-t border-white/10 bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-xl">
            <div className="p-8 space-y-8 animate-in fade-in duration-500">
              
              {/* Synthèse RH */}
              <RHSynthesis candidate={candidate} />

              {/* Profil professionnel */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h5 className="text-white font-bold mb-4 flex items-center text-lg">
                    <GraduationCap className="w-5 h-5 mr-3 text-blue-400" />
                    Formation & Éducation
                  </h5>
                  <div className="space-y-3">
                    <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <p className="text-blue-300 text-sm font-medium mb-1">Diplôme Principal</p>
                      <p className="text-white">{candidate.diplome_plus_haut || 'Non spécifié'}</p>
                    </div>
                    {candidate.domaine_etudes && (
                      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <p className="text-blue-300 text-sm font-medium mb-1">Domaine d'Études</p>
                        <p className="text-white">{candidate.domaine_etudes}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                <h5 className="text-white font-bold mb-4 flex items-center text-lg">
                    <Building className="w-5 h-5 mr-3 text-green-400" />
                    Expérience Professionnelle
                    {candidate.criteriaUsed?.selectedProfile && (
                    <span className="ml-2 text-xs text-gray-400">
                        (filtrée pour {candidate.criteriaUsed.selectedProfile})
                    </span>
                    )}
                </h5>
                <div className="space-y-2">
                    {candidate.postes && candidate.postes.length > 0 ? (
                    candidate.postes.map((poste, i) => (
                        <div key={i} className="flex items-start space-x-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <div className="flex items-center justify-center w-6 h-6 bg-green-500/30 rounded-full text-green-300 text-xs font-bold">
                            {i + 1}
                        </div>
                        <div>
                            <span className="text-white text-sm font-medium block">{poste}</span>
                            {i === 0 && candidate.postes.length > 1 && (
                            <span className="text-green-400 text-xs">Expérience la plus récente</span>
                            )}
                        </div>
                        </div>
                    ))
                    ) : (
                    <p className="text-gray-400 text-sm italic">Aucune expérience professionnelle identifiée</p>
                    )}
                    
                    {/* Affichage des entreprises si disponibles */}
                    {candidate.entreprises && candidate.entreprises.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-green-500/20">
                        <p className="text-green-300 text-xs font-medium mb-2">Entreprises mentionnées :</p>
                        <div className="flex flex-wrap gap-2">
                        {candidate.entreprises.slice(0, 3).map((ent, i) => (
                            <span key={i} className="px-2 py-1 bg-green-500/20 border border-green-500/30 rounded text-xs text-green-200">
                            {ent}
                            </span>
                        ))}
                        </div>
                    </div>
                    )}
                </div>
                </div>
              </div>

              {/* Compétences détaillées */}
              <div>
                <h5 className="text-white font-bold mb-4 flex items-center text-lg">
                  <Zap className="w-5 h-5 mr-3 text-yellow-400" />
                  Compétences Complètes
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {candidate.competences_techniques && candidate.competences_techniques.length > 0 && (
                    <div>
                      <h6 className="text-gray-300 font-medium mb-3">Compétences Techniques</h6>
                      <div className="flex flex-wrap gap-2">
                        {candidate.competences_techniques.map((comp, i) => (
                          <span key={i} className="px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-300 text-sm">
                            {comp}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {candidate.competences_soft && candidate.competences_soft.length > 0 && (
                    <div>
                      <h6 className="text-gray-300 font-medium mb-3">Compétences Comportementales</h6>
                      <div className="flex flex-wrap gap-2">
                        {candidate.competences_soft.map((comp, i) => (
                          <span key={i} className="px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-purple-300 text-sm">
                            {comp}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Langues */}
              {candidate.langues && candidate.langues.length > 0 && (
                    <div>
                        <h5 className="text-white font-bold mb-4 flex items-center text-lg">
                        <Languages className="w-5 h-5 mr-3 text-purple-400" />
                        Compétences Linguistiques
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {candidate.langues.map((lang, i) => {
                            // Gestion des différents formats de langues
                            let langueName = '';
                            let languageLevel = '';
                            
                            if (typeof lang === 'string') {
                            // Format: "Français (natif)" ou "Anglais courant"
                            const match = lang.match(/^([^(]+)(?:\s*\(([^)]+)\))?/);
                            if (match) {
                                langueName = match[1].trim();
                                languageLevel = match[2] || 'Non précisé';
                            } else {
                                langueName = lang;
                                languageLevel = 'Non précisé';
                            }
                            } else if (typeof lang === 'object' && lang.langue) {
                            // Format objet: {langue: "Français", niveau: "natif"}
                            langueName = lang.langue;
                            languageLevel = lang.niveau || 'Non précisé';
                            } else {
                            langueName = String(lang);
                            languageLevel = 'Non précisé';
                            }
                            
                            return (
                            <div key={i} className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                                <div className="flex items-center space-x-2 mb-2">
                                <Globe className="w-4 h-4 text-purple-400" />
                                <span className="text-white font-medium">{langueName}</span>
                                </div>
                                <span className="text-purple-300 text-sm">{languageLevel}</span>
                            </div>
                            );
                        })}
                        </div>
                    </div>
                    )}

              {/* Analyse de correspondance */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="text-green-400 font-bold mb-4 flex items-center text-lg">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Critères Respectés
                  </h5>
                  <div className="space-y-2">
                    {candidate.correspondance_criteres && candidate.correspondance_criteres.length > 0 ? (
                      candidate.correspondance_criteres.map((crit, i) => (
                        <div key={i} className="flex items-start space-x-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                          <span className="text-green-300 text-sm">{crit}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400 text-sm italic">Aucun critère spécifiquement respecté</p>
                    )}
                  </div>
                </div>

                <div>
                  <h5 className="text-red-400 font-bold mb-4 flex items-center text-lg">
                    <XCircle className="w-5 h-5 mr-2" />
                    Points d'Amélioration
                  </h5>
                  <div className="space-y-2">
                    {candidate.criteres_non_respectes && candidate.criteres_non_respectes.length > 0 ? (
                      candidate.criteres_non_respectes.map((crit, i) => (
                        <div key={i} className="flex items-start space-x-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                          <XCircle className="w-4 h-4 text-red-400 mt-0.5" />
                          <span className="text-red-300 text-sm">{crit}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400 text-sm italic">Aucun point négatif majeur</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions avancées */}
              <div className="flex items-center justify-between pt-6 border-t border-white/10">
                <div className="flex items-center space-x-4">
                  <button className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 rounded-lg text-white font-medium transition-all hover:scale-105 shadow-lg">
                    <Send className="w-4 h-4" />
                    <span>Contacter</span>
                  </button>
                  <button className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-lg text-white font-medium transition-all hover:scale-105 shadow-lg">
                    <Calendar className="w-4 h-4" />
                    <span>Planifier Entretien</span>
                  </button>
                </div>
                <div className="flex items-center space-x-2 text-gray-400 text-sm">
                  <Activity className="w-4 h-4" />
                  <span>Dernière mise à jour: {new Date().toLocaleDateString()}</span>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ResultsPage = () => {
  const { analysisResults } = useScreening();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScore, setFilterScore] = useState('all');
  const [showStats, setShowStats] = useState(true);
 const [expandedCards, setExpandedCards] = useState(new Set());
 // Ajout des modals
 const {
    isReportModalOpen,
    setIsReportModalOpen,
    isExportModalOpen,
    setIsExportModalOpen,
    isMailModalOpen,
    setIsMailModalOpen,
    isEmailServiceModalOpen,
    setIsEmailServiceModalOpen
  } = useModals();

  const [emailData, setEmailData] = useState(null);

  const handleSendEmails = (data) => {
    setEmailData(data);
    setIsMailModalOpen(false);
    setIsEmailServiceModalOpen(true);
  };

  const handleSelectEmailService = async (service, emailData) => {
    console.log('Service sélectionné:', service);
    console.log('Données email:', emailData);
    
    // Ici vous intégreriez l'envoi réel des emails
    // Via l'API du service sélectionné
    
    setIsEmailServiceModalOpen(false);
    
    // Notification de succès
    alert(`Emails envoyés avec succès via ${service.name}!`);
  };

 const toggleCard = (fileName) => {
   setExpandedCards(prev => {
     const newSet = new Set(prev);
     if (newSet.has(fileName)) {
       newSet.delete(fileName);
     } else {
       newSet.add(fileName);
     }
     return newSet;
   });
 };

 const filteredCandidates = analysisResults.filter(candidate => {
   const matchesSearch = (candidate.nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (candidate.prenom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (candidate.fileName || '').toLowerCase().includes(searchTerm.toLowerCase());
   
   const score = candidate.score || 0;
   const matchesFilter = filterScore === 'all' || 
                        (filterScore === 'excellent' && score >= 80) ||
                        (filterScore === 'good' && score >= 60 && score < 80) ||
                        (filterScore === 'average' && score >= 40 && score < 60) ||
                        (filterScore === 'poor' && score < 40);
   
   return matchesSearch && matchesFilter;
 });

 const stats = {
   total: analysisResults.length,
   excellent: analysisResults.filter(c => (c.score || 0) >= 80).length,
   good: analysisResults.filter(c => (c.score || 0) >= 60 && (c.score || 0) < 80).length,
   average: analysisResults.filter(c => (c.score || 0) >= 40 && (c.score || 0) < 60).length,
   poor: analysisResults.filter(c => (c.score || 0) < 40).length,
   averageScore: analysisResults.length > 0 ? Math.round(analysisResults.reduce((sum, c) => sum + (c.score || 0), 0) / analysisResults.length) : 0
 };

 return (
   <div className="min-h-screen relative bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900">
     <PremiumParticles />
     <SimpleHeader />
     
     <div className="relative z-10 pt-28 pb-12 px-4 md:px-8">
       {/* Header Premium */}
       <div className="mb-10">
         <div className="flex items-center justify-between mb-8">
           <div className="flex items-center space-x-4">
             <Link 
               to="/screening" 
               className="p-4 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-all hover:scale-105 shadow-lg backdrop-blur-sm"
             >
               <ArrowLeft className="w-6 h-6" />
             </Link>
             <div>
               <h1 className="text-5xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">
                 🎯 Résultats du Screening IA
               </h1>
               <p className="text-gray-400 text-lg flex items-center">
                 <Brain className="w-5 h-5 mr-2 text-purple-400" />
                 Analyse avancée avec IA• {filteredCandidates.length} candidats analysés
               </p>
             </div>
           </div>
           
           <div className="flex space-x-3">
        <button 
          onClick={() => setIsReportModalOpen(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 rounded-xl text-white font-medium transition-all hover:scale-105 shadow-lg"
        >
          <FileText className="w-5 h-5" />
          <span>Rapport PDF</span>
        </button>
        <button 
          onClick={() => setIsExportModalOpen(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-xl text-white font-medium transition-all hover:scale-105 shadow-lg"
        >
          <Download className="w-5 h-5" />
          <span>Export Google Sheets</span>
        </button>
        <button 
          onClick={() => setIsMailModalOpen(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 rounded-xl text-white font-medium transition-all hover:scale-105 shadow-lg"
        >
          <Send className="w-5 h-5" />
          <span>Mail Groupé ({stats.excellent})</span>
        </button>
      </div>
         </div>

         {/* Statistiques Premium */}
         {showStats && (
           <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
             <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center shadow-xl">
               <Users className="w-8 h-8 text-white mx-auto mb-2" />
               <div className="text-3xl font-bold text-white mb-1">{stats.total}</div>
               <div className="text-gray-400 text-sm font-medium">Total Candidats</div>
             </div>
             <div className="bg-gradient-to-br from-green-500/20 to-emerald-600/20 backdrop-blur-xl border border-green-500/30 rounded-2xl p-6 text-center shadow-xl">
               <Crown className="w-8 h-8 text-green-400 mx-auto mb-2" />
               <div className="text-3xl font-bold text-green-400 mb-1">{stats.excellent}</div>
               <div className="text-green-300 text-sm font-medium">Excellents (80%+)</div>
             </div>
             <div className="bg-gradient-to-br from-blue-500/20 to-cyan-600/20 backdrop-blur-xl border border-blue-500/30 rounded-2xl p-6 text-center shadow-xl">
               <Star className="w-8 h-8 text-blue-400 mx-auto mb-2" />
               <div className="text-3xl font-bold text-blue-400 mb-1">{stats.good}</div>
               <div className="text-blue-300 text-sm font-medium">Bons (60-79%)</div>
             </div>
             <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-xl border border-yellow-500/30 rounded-2xl p-6 text-center shadow-xl">
               <Target className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
               <div className="text-3xl font-bold text-yellow-400 mb-1">{stats.average}</div>
               <div className="text-yellow-300 text-sm font-medium">Moyens (40-59%)</div>
             </div>
             <div className="bg-gradient-to-br from-red-500/20 to-pink-600/20 backdrop-blur-xl border border-red-500/30 rounded-2xl p-6 text-center shadow-xl">
               <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
               <div className="text-3xl font-bold text-red-400 mb-1">{stats.poor}</div>
               <div className="text-red-300 text-sm font-medium">Faibles (&lt;40%)</div>
             </div>
             <div className="bg-gradient-to-br from-purple-500/20 to-pink-600/20 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6 text-center shadow-xl">
               <TrendingUp className="w-8 h-8 text-purple-400 mx-auto mb-2" />
               <div className="text-3xl font-bold text-purple-400 mb-1">{stats.averageScore}%</div>
               <div className="text-purple-300 text-sm font-medium">Score Moyen</div>
             </div>
           </div>
         )}

         {/* Filtres Premium */}
         <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-gradient-to-r from-white/5 to-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl">
           <div className="flex items-center space-x-4 flex-1">
             <div className="relative flex-1 max-w-md">
               <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
               <input
                 type="text"
                 placeholder="Rechercher par nom, prénom ou fichier..."
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
                 className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all backdrop-blur-sm"
               />
             </div>
             
             <select
               value={filterScore}
               onChange={(e) => setFilterScore(e.target.value)}
               className="px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all backdrop-blur-sm"
             >
               <option value="all">🎯 Tous les scores</option>
               <option value="excellent">👑 Excellents (80%+)</option>
               <option value="good">⭐ Bons (60-79%)</option>
               <option value="average">🎪 Moyens (40-59%)</option>
               <option value="poor">❌ Faibles (&lt;40%)</option>
             </select>
           </div>

           <div className="flex items-center space-x-3">
             <button
               onClick={() => setShowStats(!showStats)}
               className="flex items-center space-x-2 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-gray-300 hover:text-white transition-all backdrop-blur-sm"
             >
               <BarChart3 className="w-5 h-5" />
               <span>Statistiques</span>
             </button>
             <div className="text-gray-400 text-sm">
               {expandedCards.size > 0 && `${expandedCards.size} détail(s) ouvert(s)`}
             </div>
           </div>
         </div>
       </div>

       {/* Liste des candidats premium */}
       <div className="space-y-8">
         {filteredCandidates.map((candidate, index) => (
           <PremiumCandidateCard
             key={candidate.fileName || index}
             candidate={candidate}
             index={index}
             isExpanded={expandedCards.has(candidate.fileName)}
             onToggle={() => toggleCard(candidate.fileName)}
           />
         ))}
       </div>

       {/* États vides premium */}
       {filteredCandidates.length === 0 && analysisResults.length === 0 && (
         <div className="text-center py-20">
           <div className="w-32 h-32 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
             <Brain className="w-16 h-16 text-purple-400" />
           </div>
           <h3 className="text-3xl font-bold text-white mb-4">Aucune analyse disponible</h3>
           <p className="text-gray-400 text-lg mb-8">Commencez un nouveau screening pour découvrir les talents avec l'IA</p>
           <Link 
             to="/screening"
             className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-xl text-white font-medium transition-all hover:scale-105 shadow-lg"
           >
             <Sparkles className="w-5 h-5" />
             <span>Nouveau Screening IA</span>
           </Link>
         </div>
       )}

       {filteredCandidates.length === 0 && analysisResults.length > 0 && (
         <div className="text-center py-20">
           <div className="w-32 h-32 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
             <Search className="w-16 h-16 text-yellow-400" />
           </div>
           <h3 className="text-3xl font-bold text-white mb-4">Aucun candidat trouvé</h3>
           <p className="text-gray-400 text-lg">Modifiez vos critères de recherche pour découvrir plus de profils</p>
         </div>
       )}

     </div>
     <GroupEmailModal
        isOpen={isMailModalOpen}
        onClose={() => setIsMailModalOpen(false)}
        candidates={filteredCandidates}
        onSendEmails={handleSendEmails}
      />

      <EmailServiceModal
        isOpen={isEmailServiceModalOpen}
        onClose={() => setIsEmailServiceModalOpen(false)}
        onSelectService={handleSelectEmailService}
        emailData={emailData}
      />

      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        candidates={filteredCandidates}
      />

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        candidates={filteredCandidates}
      />
   </div>
 );
};

export default ResultsPage;